// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { AlertTriangle, Users, Zap } from 'lucide-react';
import { getConcentration } from '../api/workbenchApi';
import RefreshSection from '../components/RefreshSection';

const fmtUSD = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${n.toFixed(0)}`;
};
const fmtPct = (v) => {
  if (v == null) return '—';
  return `${(Number(v) * 100).toFixed(1)}%`;
};

const PIE_COLOURS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#94a3b8'];

const CONCENTRATION_COLOUR = {
  high: '#ef4444',
  moderate: '#f59e0b',
  low: '#22c55e',
};

const ConcentrationRisk = ({ mappingSessionId, bootstrap, onOpenDrilldown }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!mappingSessionId) return () => {};
    let cancelled = false;
    setLoading(true);
    setError(null);
    getConcentration(mappingSessionId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mappingSessionId]);

  // Auto-load on mount and whenever the workbench bootstrap mutates so the
  // owner-perk totals stay in sync after approvals.
  useEffect(() => load(), [load, bootstrap]);

  const pieData = useMemo(() => {
    if (!data?.vendors?.has_data) return [];
    const top = data.vendors.top_vendors.map((v) => ({
      name: v.vendor,
      value: Number(v.abs_total || 0),
      share: v.share,
    }));
    if (data.vendors.all_others?.vendor_count > 0) {
      top.push({
        name: `All others (${data.vendors.all_others.vendor_count})`,
        value: Number(data.vendors.all_others.total || 0),
        share: data.vendors.all_others.share,
      });
    }
    return top;
  }, [data]);

  if (loading && !data) {
    return (
      <div>
        <div className="qoe-wb-section-header">
          <h2>
            Concentration &amp; Structural Risk
            <RefreshSection onRefresh={load} busy={loading} label="Refresh concentration data" />
          </h2>
        </div>
        <div className="qoe-wb-loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div>
        <div className="qoe-wb-section-header">
          <h2>
            Concentration &amp; Structural Risk
            <RefreshSection onRefresh={load} busy={loading} label="Refresh concentration data" />
          </h2>
        </div>
        <div className="qoe-wb-row-error fail" style={{ maxWidth: 'unset' }}>
          {error}
        </div>
      </div>
    );
  }
  if (!data) return null;

  const { vendors, owner_perks, one_shot_ledger } = data;

  return (
    <div>
      <div className="qoe-wb-section-header">
        <h2>
          Concentration &amp; Structural Risk
          <RefreshSection onRefresh={load} busy={loading} label="Refresh concentration data" />
        </h2>
      </div>

      <div className="qoe-wb-conc-grid">
        {/* ---- Vendor concentration ---- */}
        <div className="qoe-wb-conc-card">
          <h3>
            <Users size={14} /> Vendor Concentration
            <span className="qoe-wb-conc-meta">
              {vendors?.scope === 'all_accounts'
                ? 'across all GL accounts'
                : 'across COGS & labor accounts'}
            </span>
          </h3>

          {!vendors?.has_data ? (
            <div className="qoe-wb-conc-empty">
              {vendors?.gl_attached === false ? (
                <>
                  No GL transactions attached to this session yet. To unlock vendor
                  concentration, open the <strong>Mapper → QoE candidates</strong> page,
                  upload a General Ledger, and the GL drill-down will auto-populate
                  here on completion.
                </>
              ) : (
                <>
                  GL transactions are attached but none could be matched to a vendor.
                  Verify the GL file's vendor column is populated.
                </>
              )}
            </div>
          ) : (
            <>
              <div className="qoe-wb-conc-hhi">
                <div>
                  <div className="qoe-wb-conc-hhi-label">HHI</div>
                  <div className="qoe-wb-conc-hhi-value">{(vendors.hhi * 10000).toFixed(0)}</div>
                </div>
                <div>
                  <div className="qoe-wb-conc-hhi-label">Concentration</div>
                  <div
                    className="qoe-wb-conc-hhi-pill"
                    style={{
                      background: `${CONCENTRATION_COLOUR[vendors.concentration_label] || '#94a3b8'}22`,
                      color: CONCENTRATION_COLOUR[vendors.concentration_label] || '#94a3b8',
                    }}
                  >
                    {vendors.concentration_label}
                  </div>
                </div>
                <div>
                  <div className="qoe-wb-conc-hhi-label">Total tracked spend</div>
                  <div className="qoe-wb-conc-hhi-value">{fmtUSD(vendors.total_spend)}</div>
                </div>
              </div>

              <div className="qoe-wb-conc-pie-row">
                <div style={{ width: 200, height: 200 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={75}
                        innerRadius={40}
                        paddingAngle={2}
                      >
                        {pieData.map((_, i) => (
                          <Cell key={i} fill={PIE_COLOURS[i % PIE_COLOURS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(v, name, ctx) => [
                          `${fmtUSD(v)} (${(ctx.payload.share * 100).toFixed(1)}%)`,
                          name,
                        ]}
                        contentStyle={{
                          background: 'var(--panel)',
                          border: '1px solid var(--border)',
                          borderRadius: 4,
                          fontSize: 12,
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <table className="qoe-wb-conc-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Vendor</th>
                      <th className="amount">Spend</th>
                      <th className="amount">Share</th>
                      <th className="amount">Txns</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendors.top_vendors.map((v, i) => (
                      <tr key={v.vendor}>
                        <td>
                          <span
                            className="qoe-wb-conc-swatch"
                            style={{ background: PIE_COLOURS[i % PIE_COLOURS.length] }}
                          />
                        </td>
                        <td>{v.vendor}</td>
                        <td className="amount">{fmtUSD(v.abs_total)}</td>
                        <td className="amount">{(v.share * 100).toFixed(1)}%</td>
                        <td className="amount">{v.txn_count}</td>
                      </tr>
                    ))}
                    {vendors.all_others?.vendor_count > 0 && (
                      <tr>
                        <td>
                          <span
                            className="qoe-wb-conc-swatch"
                            style={{ background: PIE_COLOURS[vendors.top_vendors.length % PIE_COLOURS.length] }}
                          />
                        </td>
                        <td style={{ fontStyle: 'italic', opacity: 0.7 }}>
                          All others ({vendors.all_others.vendor_count} vendors)
                        </td>
                        <td className="amount">{fmtUSD(vendors.all_others.total)}</td>
                        <td className="amount">
                          {(vendors.all_others.share * 100).toFixed(1)}%
                        </td>
                        <td className="amount">—</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* ---- Owner perks ---- */}
        <div className="qoe-wb-conc-card">
          <h3>
            <AlertTriangle size={14} /> Owner-Perk Concentration
            <span className="qoe-wb-conc-meta">approved adjustments only</span>
          </h3>
          <div className="qoe-wb-conc-perk-row">
            <div>
              <div className="qoe-wb-conc-hhi-label">Total impact</div>
              <div className="qoe-wb-conc-hhi-value">{fmtUSD(owner_perks?.owner_impact_total)}</div>
            </div>
            <div>
              <div className="qoe-wb-conc-hhi-label">% of EBITDA, as defined</div>
              <div
                className="qoe-wb-conc-hhi-value"
                style={{
                  color: owner_perks?.pct_of_ebitda > 0.15 ? '#ef4444' : 'var(--text)',
                }}
              >
                {fmtPct(owner_perks?.pct_of_ebitda)}
              </div>
            </div>
            <div>
              <div className="qoe-wb-conc-hhi-label">Contributing rows</div>
              <div className="qoe-wb-conc-hhi-value">{owner_perks?.contributing_count || 0}</div>
            </div>
          </div>
          {owner_perks?.contributing && owner_perks.contributing.length > 0 ? (
            <table className="qoe-wb-conc-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Category</th>
                  <th className="amount">Impact</th>
                </tr>
              </thead>
              <tbody>
                {owner_perks.contributing.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => onOpenDrilldown && onOpenDrilldown(c.source_account_name)}
                    style={{ cursor: onOpenDrilldown ? 'pointer' : 'default' }}
                  >
                    <td>{c.source_account_name}</td>
                    <td style={{ textTransform: 'capitalize', opacity: 0.75 }}>
                      {(c.category || '').replace(/_/g, ' ')}
                    </td>
                    <td className="amount">{fmtUSD(c.annual_impact)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="qoe-wb-conc-empty">
              No approved owner-related adjustments yet. Owner-comp / management-fee /
              donation rows will appear here once approved in the Workbench.
            </div>
          )}
        </div>

        {/* ---- One-shot ledger ---- */}
        <div className="qoe-wb-conc-card qoe-wb-conc-card-wide">
          <h3>
            <Zap size={14} /> One-Shot Vendor Ledger
            <span className="qoe-wb-conc-meta">approved full-remove rows</span>
          </h3>
          {one_shot_ledger && one_shot_ledger.length > 0 ? (
            <table className="qoe-wb-conc-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>GL</th>
                  <th>Direction</th>
                  <th className="amount">Annual impact</th>
                  <th>Reasoning</th>
                </tr>
              </thead>
              <tbody>
                {one_shot_ledger.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => onOpenDrilldown && onOpenDrilldown(row.source_account_name)}
                    style={{ cursor: onOpenDrilldown ? 'pointer' : 'default' }}
                  >
                    <td>{row.source_account_name}</td>
                    <td>{row.source_gl_account_code || '—'}</td>
                    <td style={{ textTransform: 'capitalize' }}>
                      {(row.direction || '').replace(/_/g, ' ')}
                    </td>
                    <td className="amount">{fmtUSD(row.annual_impact)}</td>
                    <td className="qoe-wb-conc-reason">{row.reasoning || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="qoe-wb-conc-empty">
              No approved full-remove adjustments yet. One-shot vendors / non-recurring
              items will appear here as they are approved in the Workbench.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConcentrationRisk;
